import React, {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import Select, { components, StylesConfig, OptionProps } from 'react-select';
import Typography from '../typography/Typography';
import Input from '../input/Input';
import CaretIcon from '../icons/CaretIcon';
import { Countries } from '../../types/services/countries';
import './phoneInput.scss';
import '../../shared/style/style.scss';

interface IData {
  value: string;
  label: string;
  dialCode: number;
  path?: string;
}

interface DropdownProps {
  path?: string;
  onChange: (number: string) => void;
  label?: string;
  initialValue?: string;
  searchLabel?: string;
  className?: string;
  fillContent?: boolean;
}

const optionsData = Countries.map((country) => ({
  value: country.country_code,
  label: country.name,
  dialCode: country.dial_code,
  path: '',
}));

const generateOptionsDataWithFlagPath = function (path: string) {
  if (path) {
    return Countries.map((country) => ({
      value: country.country_code,
      label: country.name,
      dialCode: country.dial_code,
      path: `${path}/${country.country_code.toLowerCase()}.svg`,
    }));
  }
  return optionsData;
};

const Option = (props: OptionProps<any>) => {
  const { data } = props;
  return (
    <div>
      <components.Option {...props}>
        <span
          style={data.path ? { backgroundImage: `url(${data.path})` } : {}}
          className={`m-r-8 flag-icon ${data.path ? '' : `flag-icon-${data.value.toLowerCase()}`}`}
        />
        <span className="f-w-600 m-r-7 text-ellipsis">{data.label}</span>
        {` +${data.dialCode}`}
      </components.Option>
    </div>
  );
};

const Blanket = (props: JSX.IntrinsicElements['div']) => (
  <div
    style={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      position: 'fixed',
      zIndex: 1,
    }}
    {...props}
  />
);

const DropdownIndicator = () => (
  <div style={{ color: 'grey', height: 24, width: 32 }}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      focusable="false"
      role="presentation"
    >
      <path
        d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  </div>
);

const styles: StylesConfig<any, false> = {
  control: (css) => ({
    ...css,
    height: '48px',
    padding: '0 16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: 'none',
    border: '1px solid #AFB6BE',
  }),
  indicatorsContainer: (css) => ({
    ...css,
    display: 'flex',
    order: -1,
  }),
  container: (css) => ({
    ...css,
    position: 'relative',
  }),
  menu: (css) => ({
    ...css,
    zIndex: 2,
  }),
  menuList: (css) => ({
    ...css,
    maxHeight: 200,
    paddingTop: 0,
    borderTop: '2px solid white',
  }),
  option: (css, state) => ({
    ...css,
    display: 'flex',
    alignItems: 'center',
    color: '#38414a',
    backgroundColor: state.isFocused || state.isSelected ? '#e6f4f8' : '#fff',
    '&:active': {
      backgroundColor: '#e6f4f8',
    },
  }),
};

const PhoneInput: FunctionComponent<DropdownProps> = ({
  onChange,
  initialValue,
  label = 'Your mobile number',
  searchLabel = 'Search...',
  path = '',
  className = '',
  fillContent = false,
}) => {
  const splittedPhoneValues = initialValue?.split(' ');
  let initialOption;
  const optionsDataWithPath = path ? generateOptionsDataWithFlagPath(path) : optionsData;

  if (initialValue && splittedPhoneValues && splittedPhoneValues[0]) {
    initialOption = optionsDataWithPath.find(
      (country) => country.dialCode === Number(splittedPhoneValues[0]),
    ) || optionsDataWithPath[149];
  }
  else {
    initialOption = optionsDataWithPath[149];
  }
  const [phoneInput, setPhoneInput] = useState(
    (splittedPhoneValues && splittedPhoneValues[1]) || '',
  );
  const [successInput, setSuccessInput] = React.useState(false);
  const [prefix, setPrefix] = React.useState<IData>({
    value: initialOption.value,
    label: `+${initialOption.dialCode}`,
    dialCode: initialOption.dialCode,
    path: initialOption.path,
  });

  const [isOpen, setisOpen] = useState(false);
  const [focus, setFocus] = useState(false);

  const handlePrefix = (selectOption: IData) => {
    setisOpen(!isOpen);
    setPrefix({
      value: selectOption.value,
      label: `+${selectOption.dialCode}`,
      dialCode: selectOption.dialCode,
      path: selectOption.path,
    });
  };

  useEffect(() => {
    if (!prefix?.dialCode || !phoneInput || phoneInput.length < 6) {
      setSuccessInput(false);

      return;
    }

    setSuccessInput(true);

    const phoneNumber = `+${prefix.dialCode} ${phoneInput.replace(/ /g, '')}`;

    if (onChange) onChange(phoneNumber);
  }, [prefix, phoneInput]);

  return (
    <div className={`phone-input-wrapper ${className}`}>
      <div
        className={`custom-dropdown-phone-input d-flex align-center p-relative ${successInput ? 'success' : ''
        } ${focus ? 'focus' : ''} ${isOpen ? 'focus' : ''}`}
        data-testid="custom-dropdown-phone-input"
      >
        <Typography
          variant="caption"
          className="label p-absolute"
          color={successInput ? 'atlantis5' : 'asphalt6'}
        >
          {label}
        </Typography>
        <div className="custom-dropdown-countries">
          <div data-testid="dropdown_country-code" className="dropdown-wrapper" onClick={() => setisOpen(!isOpen)}>
            <span
              style={{ backgroundImage: `url(${prefix.path})` }}
              className={`flag-icon ${prefix.path ? '' : `flag-icon-${prefix.value.toLowerCase()}`}`}
            />
            <span className="f-w-600">{`+${prefix.dialCode}`}</span>
            <CaretIcon direction="bottom" width={7} />
          </div>
        </div>
        <Input
          name="countries"
          type="number"
          className="phone-number-input flex-grow-2"
          confirmed={successInput}
          onChange={(e) => {
            // @ts-ignore
            setPhoneInput(e?.currentTarget?.value);
          }}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          value={phoneInput}
          onWheel={(e: WheelEvent) => (e.target as HTMLInputElement).blur()}
          data-testid="input_phone-number"
        />
      </div>
      {isOpen
        ? (
          <div className={`menu-wrapper ${fillContent ? 'fill-content' : 'a-position'}`}>
            <Select
              components={{
                Option,
                DropdownIndicator,
                IndicatorSeparator: null,
              }}
              onChange={handlePrefix}
              options={optionsDataWithPath}
              styles={styles}
              className="select-dropdown f-s-16"
              value={prefix}
              isMulti={false}
              isSearchable
              autoFocus
              backspaceRemovesValue={false}
              controlShouldRenderValue={false}
              hideSelectedOptions={false}
              isClearable={false}
              menuIsOpen
              placeholder={searchLabel}
              tabSelectsValue={false}
              filterOption={(candidate: any, input: string) => {
                if (input[0] === '+') {
                  input = input.substring(1);
                }
                if (!Number.isNaN(Number(input[0]))) {
                  return candidate.data.dialCode.toString().includes(input);
                }
                return candidate.label.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </div>
        )
        : null}
      {isOpen ? <Blanket onClick={() => setisOpen(!isOpen)} /> : null}
    </div>
  );
};

export default PhoneInput;

PhoneInput.displayName = 'PhoneInput';
